import React, { useState, useMemo, useRef, useLayoutEffect } from 'react'
import { Box, Typography, Portal } from '@mui/material'
import { ReactComponent as CalendarIcon } from '../../assets/appointment-icons/calendar.svg'
import { ReactComponent as ChevronIcon } from '../../assets/appointment-icons/chevron.svg'
import { useLavMobile } from './LavDropdown'

/**
 * DropdownCalendar — a compact "dropdown calendar" component recreated from the
 * Claude Design "Add Appointment Flow" (ApDatePicker). A trigger button reveals
 * a single-month calendar popover with prev/next navigation, today marker and a
 * footer summary. Styled with the lavender design tokens.
 *
 * @param {Object} props
 * @param {string} props.value - Selected date as YYYY-MM-DD (or '' / null)
 * @param {Function} props.onChange - Called with the new YYYY-MM-DD string
 * @param {string} [props.placeholder='Pick a date'] - Empty-state label
 * @param {boolean} [props.small] - Compact trigger height
 * @param {boolean} [props.up] - Open the popover above the trigger
 * @param {'left'|'right'} [props.align='left'] - Horizontal alignment of popover
 * @param {string} [props.min] - Minimum selectable date (YYYY-MM-DD)
 * @param {boolean} [props.defaultOpen] - Start open
 * @param {boolean} [props.pinned] - Always open, rendered inline (no backdrop)
 * @param {boolean} [props.disabled] - Disable the trigger
 * @param {Function} [props.fmt] - Trigger label formatter (Date → string); defaults to 'Tue, Jun 9'
 * @param {Object} [props.sx] - Extra sx applied to the root container
 */

// Lavender design tokens (shared vocabulary with the checkout drawer)
const L = {
	bg: '#FDFCFF',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	onPrimary: '#FFFFFF',
	field: '#FFFFFF',
	font: "'Outfit', sans-serif",
}

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3))
// Years shown per page in the year picker (3 columns × 4 rows).
const YEAR_PAGE = 12
// Approximate popover height, used only to decide whether to flip above the
// trigger when there isn't room below.
const POP_H = 330

function parseDate(s) {
	const [y, m, d] = (s || '').split('-').map(Number)
	return new Date(y || 2026, (m || 1) - 1, d || 1)
}
function dateToStr(d) {
	return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
function fmtFull(d) {
	return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Icon set sourced from src/assets/appointment-icons (exact design glyphs)
const ICONS = { calendar: CalendarIcon, chevron: ChevronIcon }
function Icon({ name, size = 15 }) {
	const Cmp = ICONS[name]
	if (!Cmp) return null
	return <Cmp width={size} height={size} style={{ flex: 'none', display: 'block' }} aria-hidden="true" />
}

export default function DropdownCalendar({
	value,
	onChange,
	placeholder = 'Pick a date',
	small = false,
	up = false,
	align = 'left',
	min,
	max,
	defaultOpen = false,
	pinned = false,
	disabled = false,
	fmt = null,
	sx = {},
}) {
	// Touch sizing (design `.mbap` overrides) when rendered inside a mobile shell.
	const mobile = useLavMobile()
	const [open, setOpen] = useState(Boolean(defaultOpen) || Boolean(pinned))
	const triggerRef = useRef(null)
	const [rect, setRect] = useState(null)
	const sel = value ? parseDate(value) : null

	const [view, setView] = useState(() => {
		const base = sel || (min ? parseDate(min) : new Date())
		return { y: base.getFullYear(), m: base.getMonth() }
	})

	// Header drill-down: the month and year labels each open their own picker,
	// matching the convention in most date pickers. 'day' is the calendar grid.
	const [mode, setMode] = useState('day')
	// First year of the visible page in the year picker.
	const [yearStart, setYearStart] = useState(() => view.y - 6)

	const toggleOpen = () => {
		if (disabled) return
		if (open && !pinned) { setOpen(false); return }
		const base = sel || (min ? parseDate(min) : new Date())
		setView({ y: base.getFullYear(), m: base.getMonth() })
		setMode('day')
		setOpen(true)
	}

	// Track the trigger's viewport box so the portalled popover can follow it
	// through scrolling and resizing.
	useLayoutEffect(() => {
		if (!open || pinned) return undefined
		const measure = () => {
			if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect())
		}
		measure()
		// Capture phase so nested scroll containers are heard, not just window.
		window.addEventListener('scroll', measure, true)
		window.addEventListener('resize', measure)
		return () => {
			window.removeEventListener('scroll', measure, true)
			window.removeEventListener('resize', measure)
		}
	}, [open, pinned])

	const today = new Date()
	const todayStr = dateToStr(today)

	const cells = useMemo(() => {
		const first = new Date(view.y, view.m, 1)
		const startDow = first.getDay()
		const daysIn = new Date(view.y, view.m + 1, 0).getDate()
		const weeks = Math.ceil((startDow + daysIn) / 7)
		const out = []
		for (let i = 0; i < weeks * 7; i++) out.push(new Date(view.y, view.m, 1 - startDow + i))
		return out
	}, [view])

	const pick = (d) => {
		onChange?.(dateToStr(d))
		if (!pinned) setOpen(false)
	}

	// A month/year is only unreachable when the *whole* span falls outside
	// [min, max] — otherwise it holds at least one selectable day.
	const monthDisabled = (y, m) => {
		if (min && dateToStr(new Date(y, m + 1, 0)) < min) return true
		if (max && dateToStr(new Date(y, m, 1)) > max) return true
		return false
	}
	const yearDisabled = (y) => {
		if (min && `${y}-12-31` < min) return true
		if (max && `${y}-01-01` > max) return true
		return false
	}

	const openMonthMode = () => setMode(mode === 'month' ? 'day' : 'month')
	const openYearMode = () => {
		if (mode === 'year') { setMode('day'); return }
		setYearStart(view.y - 6)
		setMode('year')
	}

	// Prev/next step by month, year or year-page depending on the active picker.
	const stepBy = (dir) => {
		if (mode === 'day') {
			setView(dir < 0
				? (view.m === 0 ? { y: view.y - 1, m: 11 } : { y: view.y, m: view.m - 1 })
				: (view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 }))
		} else if (mode === 'month') {
			setView({ ...view, y: view.y + dir })
		} else {
			setYearStart((s) => s + dir * YEAR_PAGE)
		}
	}
	const stepLabel = mode === 'day' ? 'month' : mode === 'month' ? 'year' : 'years'

	// ----- trigger -----
	// Mobile touch sizes: regular triggers 50px, `small` (inline visit editor) 44px.
	const triggerSx = {
		display: 'flex', alignItems: 'center', gap: small ? '7px' : '9px', width: '100%',
		height: small ? (mobile ? '44px' : '36px') : (mobile ? '50px' : '42px'),
		px: small ? (mobile ? '11px' : '10px') : (mobile ? '13px' : '12px'),
		border: `1.5px solid ${open ? L.primary : L.border}`,
		borderRadius: small ? (mobile ? '11px' : '10px') : (mobile ? '12px' : '10px'),
		bgcolor: L.field, fontFamily: L.font,
		fontSize: small ? (mobile ? '13px' : '12.5px') : (mobile ? '15px' : '14px'),
		fontWeight: 600, color: L.text, cursor: disabled ? 'default' : 'pointer',
		boxShadow: open ? `0 0 0 3px ${L.primarySoft}` : 'none',
		opacity: disabled ? 0.55 : 1,
		transition: 'border-color .15s, box-shadow .15s',
		'&:hover': { borderColor: disabled ? L.border : L.primary },
		'& > svg': { color: L.muted },
	}

	const popW = mobile ? 300 : 276

	// Floating popover placement. `pinned` renders inline, so it stays in normal
	// flow; otherwise the popover is portalled to <body> with viewport
	// coordinates. Anchoring in-flow would let any scroll container above the
	// trigger clip it — MUI's DialogContent (overflow-y: auto) does exactly
	// that to the pet form's date of birth field.
	// Passed via `style`, not `sx`: MUI's sx treats top/left/bottom as
	// spacing-scale values and would multiply these pixel figures by 8.
	const anchorStyle = () => {
		if (!rect) return { visibility: 'hidden', top: 0, left: 0 }
		const room = window.innerHeight - rect.bottom
		// Flip above when the popover would not fit below but does fit above.
		const flip = up || (room < POP_H && rect.top > room)
		return {
			left: `${Math.round(Math.max(8, Math.min(
				align === 'right' ? rect.right - popW : rect.left,
				window.innerWidth - popW - 8
			)))}px`,
			...(flip
				? { bottom: `${Math.round(Math.max(8, window.innerHeight - rect.top + 6))}px` }
				: { top: `${Math.round(rect.bottom + 6)}px` }),
		}
	}

	const popSx = {
		bgcolor: '#FFFFFF',
		border: `1px solid ${L.border}`, borderRadius: mobile ? '16px' : '14px',
		p: '12px', width: `${popW}px`, maxWidth: 'calc(100vw - 16px)',
		boxShadow: '0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)',
		...(pinned
			? {
				position: 'absolute', zIndex: 30,
				left: align === 'right' ? 'auto' : 0,
				right: align === 'right' ? 0 : 'auto',
				...(up ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
			}
			: { position: 'fixed', zIndex: 1500 }),
	}

	// The popover and its backdrop live at <body> level unless pinned.
	const Wrap = pinned ? React.Fragment : Portal

	return (
		<Box sx={{ position: 'relative', fontFamily: L.font, ...sx }}>
			<Box
				component="button"
				type="button"
				ref={triggerRef}
				aria-expanded={open}
				disabled={disabled}
				onClick={toggleOpen}
				sx={triggerSx}
			>
				<Icon name="calendar" size={small ? 14 : 15} />
				<Box component="span" sx={{
					flex: 1, textAlign: 'left', minWidth: 0, overflow: 'hidden',
					textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					color: sel ? L.text : L.muted, fontWeight: sel ? 600 : 500,
				}}>
					{sel ? (fmt || fmtFull)(sel) : placeholder}
				</Box>
				<Box component="span" sx={{
					display: 'flex', color: L.muted, transition: 'transform .15s',
					transform: open ? 'rotate(180deg)' : 'none',
				}}>
					<Icon name="chevron" size={14} />
				</Box>
			</Box>

			{open && (
				<Wrap>
					{!pinned && (
						<Box
							onClick={() => setOpen(false)}
							sx={{ position: 'fixed', inset: 0, zIndex: 1499 }}
						/>
					)}
					<Box style={pinned ? undefined : anchorStyle()} sx={popSx}>
						{/* head — month and year each drill into their own picker */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', mb: '8px', pl: '4px' }}>
							<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
								<Box
									component="button"
									type="button"
									aria-label="Select month"
									aria-expanded={mode === 'month'}
									onClick={openMonthMode}
									sx={headBtnSx(mode === 'month', mobile)}
								>
									<span>{MONTHS[view.m]}</span>
									<HeadCaret open={mode === 'month'} mobile={mobile} />
								</Box>
								<Box
									component="button"
									type="button"
									aria-label="Select year"
									aria-expanded={mode === 'year'}
									onClick={openYearMode}
									sx={headBtnSx(mode === 'year', mobile)}
								>
									<span>{view.y}</span>
									<HeadCaret open={mode === 'year'} mobile={mobile} />
								</Box>
							</Box>
							<Box
								component="button"
								type="button"
								aria-label={`Previous ${stepLabel}`}
								onClick={() => stepBy(-1)}
								sx={{ ...navSx, ...(mobile ? navMobileSx : {}) }}
							>
								<Box component="span" sx={{ display: 'flex', transform: 'rotate(90deg)' }}>
									<Icon name="chevron" size={13} />
								</Box>
							</Box>
							<Box
								component="button"
								type="button"
								aria-label={`Next ${stepLabel}`}
								onClick={() => stepBy(1)}
								sx={{ ...navSx, ...(mobile ? navMobileSx : {}) }}
							>
								<Box component="span" sx={{ display: 'flex', transform: 'rotate(-90deg)' }}>
									<Icon name="chevron" size={13} />
								</Box>
							</Box>
						</Box>

						{/* month picker */}
						{mode === 'month' && (
							<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
								{MONTHS_SHORT.map((label, m) => {
									const isDisabled = monthDisabled(view.y, m)
									const on = m === view.m
									return (
										<Box
											component="button"
											type="button"
											key={label}
											disabled={isDisabled}
											aria-label={`${MONTHS[m]} ${view.y}`}
											onClick={() => { setView({ ...view, m }); setMode('day') }}
											sx={gridBtnSx(on, isDisabled, mobile)}
										>
											{label}
										</Box>
									)
								})}
							</Box>
						)}

						{/* year picker */}
						{mode === 'year' && (
							<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
								{Array.from({ length: YEAR_PAGE }, (_, i) => yearStart + i).map((y) => {
									const isDisabled = yearDisabled(y)
									const on = y === view.y
									return (
										<Box
											component="button"
											type="button"
											key={y}
											disabled={isDisabled}
											aria-label={String(y)}
											onClick={() => { setView({ ...view, y }); setMode('day') }}
											sx={gridBtnSx(on, isDisabled, mobile)}
										>
											{y}
										</Box>
									)
								})}
							</Box>
						)}

						{/* grid */}
						{mode === 'day' && (
						<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
							{DOW.map((d) => (
								<Box key={d} sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'center',
									height: mobile ? '28px' : '24px',
									fontSize: mobile ? '11px' : '10px', fontWeight: 750, color: L.muted, fontFamily: L.font,
								}}>
									{d}
								</Box>
							))}
							{cells.map((d) => {
								const ds = dateToStr(d)
								const isSel = value === ds
								const isOut = d.getMonth() !== view.m
								const isToday = ds === todayStr && !isSel
								const isDisabled = (Boolean(min) && ds < min) || (Boolean(max) && ds > max)
								return (
									<Box
										component="button"
										type="button"
										key={ds}
										disabled={isDisabled}
										aria-label={fmtFull(d)}
										onClick={() => pick(d)}
										sx={{
											position: 'relative', height: mobile ? '42px' : '32px', border: 'none',
											borderRadius: mobile ? '11px' : '9px',
											bgcolor: isSel ? L.primary : 'transparent',
											color: isDisabled ? '#DAD5EA' : isSel ? L.onPrimary : isOut ? '#C7C1DB' : L.text,
											fontFamily: L.font, fontSize: mobile ? '14.5px' : '12.5px', fontWeight: isSel ? 750 : 600,
											cursor: isDisabled ? 'default' : 'pointer',
											fontVariantNumeric: 'tabular-nums',
											'&:hover': { bgcolor: isDisabled ? 'transparent' : isSel ? L.primaryDeep : L.surface },
											'&::after': isToday ? {
												content: '""', position: 'absolute', bottom: mobile ? '6px' : '4px', left: '50%',
												transform: 'translateX(-50%)', width: mobile ? '5px' : '4px', height: mobile ? '5px' : '4px',
												borderRadius: '50%', bgcolor: L.primary,
											} : undefined,
										}}
									>
										{d.getDate()}
									</Box>
								)
							})}
						</Box>
						)}

						{/* foot */}
						<Box sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							mt: '9px', pt: '9px', borderTop: `1px solid ${L.surface}`,
						}}>
							<Typography sx={{ fontSize: mobile ? '12.5px' : '11.5px', fontWeight: 650, color: L.muted, fontFamily: L.font }}>
								{sel ? fmtFull(sel) : 'Nothing selected'}
							</Typography>
							{(!min || todayStr >= min) && (!max || todayStr <= max) && (
								<Box
									component="button"
									type="button"
									onClick={() => pick(today)}
									sx={{
										border: 'none', bgcolor: 'transparent', color: L.primaryDeep,
										fontSize: mobile ? '13px' : '12px', fontWeight: 700, fontFamily: L.font, cursor: 'pointer',
										borderRadius: '8px', px: '8px', py: '4px',
										'&:hover': { bgcolor: L.primarySoft },
									}}
								>
									Today
								</Box>
							)}
						</Box>
					</Box>
				</Wrap>
			)}
		</Box>
	)
}

const navSx = {
	width: '28px', height: '28px', flex: 'none', border: 'none', borderRadius: '8px',
	bgcolor: 'transparent', color: L.muted, display: 'flex', alignItems: 'center',
	justifyContent: 'center', cursor: 'pointer',
	'&:hover': { bgcolor: L.surface, color: L.text },
}

// Caret next to the month / year labels — the affordance that says these
// labels are themselves dropdowns. Flips when its picker is open.
function HeadCaret({ open, mobile }) {
	return (
		<Box
			component="span"
			sx={{
				display: 'flex', flex: 'none', color: open ? L.primaryDeep : L.muted,
				transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none',
			}}
		>
			<Icon name="chevron" size={mobile ? 12 : 11} />
		</Box>
	)
}

// Clickable month / year labels in the popover header.
const headBtnSx = (active, mobile) => ({
	display: 'inline-flex', alignItems: 'center', gap: mobile ? '4px' : '3px',
	border: 'none', borderRadius: '7px', px: '6px', py: '3px',
	bgcolor: active ? L.primarySoft : 'transparent',
	color: active ? L.primaryDeep : L.text,
	fontFamily: L.font, fontSize: mobile ? '15px' : '13.5px', fontWeight: 750,
	letterSpacing: '-0.01em', cursor: 'pointer', whiteSpace: 'nowrap',
	'&:hover': { bgcolor: active ? L.primarySoft : L.surface },
})

// Cells in the month / year pickers.
const gridBtnSx = (selected, isDisabled, mobile) => ({
	height: mobile ? '44px' : '36px', border: 'none', borderRadius: mobile ? '11px' : '9px',
	bgcolor: selected ? L.primary : 'transparent',
	color: isDisabled ? '#DAD5EA' : selected ? L.onPrimary : L.text,
	fontFamily: L.font, fontSize: mobile ? '14px' : '12.5px', fontWeight: selected ? 750 : 650,
	cursor: isDisabled ? 'default' : 'pointer', fontVariantNumeric: 'tabular-nums',
	'&:hover': { bgcolor: isDisabled ? 'transparent' : selected ? L.primaryDeep : L.surface },
})

const navMobileSx = { width: '34px', height: '34px', borderRadius: '10px' }
